import { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import type { HeatmapPoint } from '@/types/dashboard'

interface ActivityTopDaysProps {
  days: HeatmapPoint[]
  limit?: number
  onDayClick?: (date: string) => void
}

/**
 * Топ самых активных дней периода — считается из уже загруженных точек
 * heatmap, без отдельного запроса к API.
 */
export function ActivityTopDays({ days, limit = 10, onDayClick }: ActivityTopDaysProps) {
  const { t } = useTranslation()

  const top = useMemo(
    () =>
      [...days]
        .filter((point) => point.count > 0)
        .sort((a, b) => b.count - a.count || (a.date < b.date ? 1 : -1))
        .slice(0, limit),
    [days, limit],
  )

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {t('activity.topDays')}
      </Typography>
      {top.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('activity.empty')}
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {top.map((point) => (
            <Box
              key={point.date}
              onClick={onDayClick ? () => onDayClick(point.date) : undefined}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                cursor: onDayClick ? 'pointer' : 'default',
                '&:hover': onDayClick ? { backgroundColor: 'action.hover' } : undefined,
              }}
            >
              <Typography variant="body2">{dayjs(point.date).format('DD.MM.YYYY')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('activity.eventsCount', { count: point.count })}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}
